import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  FormControlLabel,
  Switch,
  Checkbox,
  Box,
} from '@material-ui/core';
import cloneDeep from 'lodash/cloneDeep';
import { BaseFilter, CodeFilters } from '../../lib/orderListCodeFilters';
import { getCustomerOrderStatus, getCustomerOrderCoreUsers } from '../../api';
import { handleError, checkStatus, parseJSON } from '../../../shared/api/core/common';

export type OrderListFilterApplyPayload = {
  codeQuery: string | null;
  statusFilter: string | null;
  userFilter: string | null;
  activeCodeCount: number;
};

interface Props {
  open: boolean;
  onClose: () => void;
  businessId: number | string;
  locationId: string;
  onApply: (payload: OrderListFilterApplyPayload) => void;
}

const OrderListFilterModal = (props: Props) => {
  const { open, onClose, businessId, locationId, onApply } = props;
  const [codeFilters, setCodeFilters] = useState<BaseFilter[]>(() => cloneDeep(CodeFilters));
  const [showStatus, setShowStatus] = useState(false);
  const [showUsers, setShowUsers] = useState(false);
  const [statusList, setStatusList] = useState<any[]>([]);
  const [selectedStatus, setSelectedStatus] = useState<any[]>([]);
  const [users, setUsers] = useState<any[]>([]);
  const [selectedUsers, setSelectedUsers] = useState<any[]>([]);

  useEffect(() => {
    if (open) {
      setCodeFilters(cloneDeep(CodeFilters));
      getCustomerOrderStatus('')
        .catch(handleError)
        .then(checkStatus)
        .then(parseJSON)
        .then((resp: any) => setStatusList(resp || []))
        .catch(() => {});
    }
  }, [open]);

  const fetchUsers = (statuses: any[]) => {
    if (!statuses || statuses.length === 0) {
      setUsers([]);
      return;
    }
    getCustomerOrderCoreUsers({
      selected_status: statuses,
      business_id: businessId,
      location_id: locationId,
      search_term: '',
    })
      .catch(handleError)
      .then(checkStatus)
      .then(parseJSON)
      .then((resp: any) => setUsers(resp || []))
      .catch(() => {});
  };

  const toggleCode = (id: number) => {
    setCodeFilters((prev) =>
      prev.map((f) => (f.id === id ? { ...f, active: !f.active } : f)),
    );
  };

  const apply = () => {
    let codeQuery: string | null = null;
    let activeCodeCount = 0;
    codeFilters.forEach((f) => {
      if (f.active) {
        activeCodeCount++;
        codeQuery = codeQuery ? codeQuery + f.constraint : f.constraint;
      }
    });
    let statusFilter: string | null = null;
    if (selectedStatus.length > 0) {
      statusFilter = `&customer_order_status__id__in=${selectedStatus.map((i) => i.id).join(',')}`;
    }
    let userFilter: string | null = null;
    if (selectedUsers.length > 0) {
      userFilter = `&order_status_entry_user_id__in=${selectedUsers.map((i) => i.id).join(',')}`;
    }
    onApply({
      codeQuery,
      statusFilter,
      userFilter,
      activeCodeCount,
    });
    onClose();
  };

  const reset = () => {
    setCodeFilters(cloneDeep(CodeFilters));
    setShowStatus(false);
    setShowUsers(false);
    setSelectedStatus([]);
    setSelectedUsers([]);
    setUsers([]);
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Order list filters</DialogTitle>
      <DialogContent>
        <Typography variant="subtitle2" gutterBottom>
          Workflow stage
        </Typography>
        <Box display="flex" flexDirection="column">
          {codeFilters.map((f) => (
            <FormControlLabel
              key={f.id}
              control={
                <Checkbox
                  checked={f.active}
                  onChange={() => toggleCode(f.id)}
                  color="primary"
                />
              }
              label={f.displayText}
            />
          ))}
        </Box>

        <Box display="flex" alignItems="center" flexWrap="wrap" mt={2}>
          <Typography variant="body2" style={{ marginRight: 8 }}>
            Status
          </Typography>
          <Switch
            checked={showStatus}
            onChange={() => {
              setShowStatus(!showStatus);
              setSelectedStatus([]);
              setUsers([]);
            }}
            color="primary"
          />
          <Typography variant="body2" style={{ marginRight: 8, marginLeft: 16 }}>
            Users
          </Typography>
          <Switch
            checked={showUsers}
            onChange={() => {
              if (showStatus && selectedStatus.length > 0) {
                setShowUsers(!showUsers);
              }
            }}
            disabled={!showStatus || selectedStatus.length === 0}
            color="primary"
          />
        </Box>

        {showStatus && (
          <Box mt={1}>
            {statusList.map((s) => (
              <FormControlLabel
                key={s.id}
                control={
                  <Checkbox
                    checked={!!s.isSelected}
                    onChange={() => {
                      setStatusList((prev) =>
                        prev.map((x) =>
                          x.id === s.id ? { ...x, isSelected: !x.isSelected } : x,
                        ),
                      );
                    }}
                    color="primary"
                  />
                }
                label={s.value}
              />
            ))}
            <Button
              size="small"
              color="primary"
              onClick={() => {
                const sel = statusList.filter((b) => b.isSelected);
                const merged = [...selectedStatus];
                sel.forEach((s) => {
                  if (!merged.find((m) => m.id === s.id)) {
                    merged.push(s);
                  }
                });
                setSelectedStatus(merged);
                fetchUsers(merged);
              }}
            >
              Add selected statuses
            </Button>
            <Box mt={1} display="flex" flexWrap="wrap">
              {selectedStatus.map((s) => (
                <Button
                  key={s.id}
                  size="small"
                  style={{ marginRight: 4, marginBottom: 4 }}
                  onClick={() => {
                    setSelectedStatus(selectedStatus.filter((x) => x.id !== s.id));
                  }}
                >
                  {s.value} ×
                </Button>
              ))}
            </Box>
          </Box>
        )}

        {showUsers && selectedStatus.length > 0 && (
          <Box mt={1}>
            {users.map((u) => (
              <FormControlLabel
                key={u.id}
                control={
                  <Checkbox
                    checked={!!u.isSelected}
                    onChange={() => {
                      setUsers((prev) =>
                        prev.map((x) =>
                          x.id === u.id ? { ...x, isSelected: !x.isSelected } : x,
                        ),
                      );
                    }}
                    color="primary"
                  />
                }
                label={`${u.first_name} ${u.last_name}`}
              />
            ))}
            <Button
              size="small"
              color="primary"
              onClick={() => {
                const sel = users.filter((b) => b.isSelected);
                const merged = [...selectedUsers];
                sel.forEach((u) => {
                  if (!merged.find((m) => m.id === u.id)) {
                    merged.push(u);
                  }
                });
                setSelectedUsers(merged);
              }}
            >
              Add selected users
            </Button>
            <Box mt={1} display="flex" flexWrap="wrap">
              {selectedUsers.map((u) => (
                <Button
                  key={u.id}
                  size="small"
                  style={{ marginRight: 4, marginBottom: 4 }}
                  onClick={() =>
                    setSelectedUsers(selectedUsers.filter((x) => x.id !== u.id))
                  }
                >
                  {u.first_name} ×
                </Button>
              ))}
            </Box>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={reset}>Reset</Button>
        <Button onClick={onClose}>Cancel</Button>
        <Button color="primary" variant="contained" onClick={apply}>
          Apply
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default OrderListFilterModal;
